import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Plus, Pencil, Trash2, Users, Calendar, Package, MessageSquare, ShoppingBag, ChevronDown, ChevronUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useR2Upload } from '@/hooks/useR2Upload';
import AdminBookingsTab from '@/features/admin/components/AdminBookingsTab';
import AdminSupportTicketsTab from '@/features/admin/components/AdminSupportTicketsTab';

const API_URL = process.env.REACT_APP_API_URL;

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [designers, setDesigners] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [designerDialogOpen, setDesignerDialogOpen] = useState(false);
  const [editingDesigner, setEditingDesigner] = useState(null);
  const [designerFormData, setDesignerFormData] = useState({
    name: '',
    bio: '',
    experience: '',
    specialties: '',
    hourly_rate: '',
    profile_images_urls: [],
    design_images_urls: []
  });
  const [profileFiles, setProfileFiles] = useState([]);
  const [designFiles, setDesignFiles] = useState([]);
  const { uploading, progress, uploadMultiple } = useR2Upload();

  // Products state
  const [products, setProducts] = useState([]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productFormData, setProductFormData] = useState({
    name: '',
    description: '',
    price: '',
    categories: '',
    colors: '',
    sizes: '',
    images_urls: []
  });
  const [productFiles, setProductFiles] = useState([]);
  const [ticketNotes, setTicketNotes] = useState({});
  const [privateTicketNotes, setPrivateTicketNotes] = useState({});
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  useEffect(() => {
    fetchDesigners();
    fetchAllBookings();
    fetchAllOrders();
    fetchProducts();
    fetchSupportTickets();
  }, []);

  const fetchDesigners = async () => {
    try {
      const response = await axios.get(`${API_URL}/designers`);
      setDesigners(response.data);
    } catch (error) {
      console.error('Error fetching designers:', error);
      toast.error('Failed to load designers');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    }
  };

  const fetchAllBookings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchAllOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    }
  };

  const fetchSupportTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/support-tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupportTickets(response.data || []);
      setTicketNotes(
        (response.data || []).reduce((acc, ticket) => {
          acc[ticket.id] = '';
          return acc;
        }, {})
      );
      setPrivateTicketNotes(
        (response.data || []).reduce((acc, ticket) => {
          acc[ticket.id] = '';
          return acc;
        }, {})
      );
    } catch (error) {
      console.error('Error fetching support tickets:', error);
    }
  };

  const handleSubmitDesigner = async () => {
    try {
      const token = localStorage.getItem('token');

      // Upload images directly to R2
      const uploadImagesR2 = async (files) => {
        if (!files || files.length === 0) return [];
        try {
          // Upload directly to R2
          const urls = await uploadMultiple(files, token);
          return urls;
        } catch (error) {
          console.error('R2 upload error:', error);
          // Fallback to backend upload if R2 fails
          toast.warning('Using fallback upload method');
          const form = new FormData();
          files.forEach((f) => form.append('files', f));
          const res = await axios.post(`${API_URL}/upload-images`, form, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
          return res.data.urls || [];
        }
      };

      // Upload new images if selected, and merge with existing URLs (on edit)
      const [newProfileUrls, newDesignUrls] = await Promise.all([
        uploadImagesR2(profileFiles),
        uploadImagesR2(designFiles)
      ]);

      const profile_images = [...(designerFormData.profile_images_urls || []), ...newProfileUrls];
      const design_images = [...(designerFormData.design_images_urls || []), ...newDesignUrls];

      const payload = {
        name: designerFormData.name,
        bio: designerFormData.bio,
        experience: designerFormData.experience,
        specialties: designerFormData.specialties.split(',').map(s => s.trim()).filter(Boolean),
        hourly_rate: parseFloat(designerFormData.hourly_rate),
        profile_images,
        design_images
      };

      if (editingDesigner) {
        await axios.put(`${API_URL}/admin/designers/${editingDesigner.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Designer updated successfully!');
      } else {
        await axios.post(`${API_URL}/admin/designers`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Designer created successfully!');
      }

      setDesignerDialogOpen(false);
      setEditingDesigner(null);
      setDesignerFormData({
        name: '',
        bio: '',
        experience: '',
        specialties: '',
        hourly_rate: '',
        profile_images_urls: [],
        design_images_urls: []
      });
      setProfileFiles([]);
      setDesignFiles([]);
      fetchDesigners();
    } catch (error) {
      console.error('Error saving designer:', error);
      toast.error(error.response?.data?.detail || 'Failed to save designer');
    }
  };

  const handleEditDesigner = (designer) => {
    setEditingDesigner(designer);
    const profile = designer.profile_images && designer.profile_images.length > 0 ? designer.profile_images : (designer.portfolio_images || []);
    const designs = designer.design_images && designer.design_images.length > 0 ? designer.design_images : (designer.portfolio_images ? designer.portfolio_images.slice(1) : []);
    setDesignerFormData({
      name: designer.name,
      bio: designer.bio,
      experience: designer.experience,
      specialties: designer.specialties.join(', '),
      hourly_rate: designer.hourly_rate.toString(),
      profile_images_urls: profile,
      design_images_urls: designs
    });
    setProfileFiles([]);
    setDesignFiles([]);
    setDesignerDialogOpen(true);
  };

  const handleDeleteDesigner = async (designerId) => {
    if (!window.confirm('Are you sure you want to delete this designer?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/designers/${designerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Designer deleted successfully!');
      fetchDesigners();
    } catch (error) {
      console.error('Error deleting designer:', error);
      toast.error('Failed to delete designer');
    }
  };

  const handleSubmitProduct = async () => {
    try {
      const token = localStorage.getItem('token');

      // Upload images directly to R2 (same as designer images)
      const uploadImagesR2 = async (files) => {
        if (!files || files.length === 0) return [];
        try {
          // Upload directly to R2
          const urls = await uploadMultiple(files, token);
          return urls;
        } catch (error) {
          console.error('R2 upload error:', error);
          // Fallback to backend upload if R2 fails
          toast.warning('Using fallback upload method');
          const form = new FormData();
          files.forEach((f) => form.append('files', f));
          const res = await axios.post(`${API_URL}/upload-images`, form, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
          return res.data.urls || [];
        }
      };

      const newImageUrls = await uploadImagesR2(productFiles);
      const images = [...(productFormData.images_urls || []), ...newImageUrls];

      const payload = {
        name: productFormData.name,
        description: productFormData.description,
        price: parseFloat(productFormData.price),
        categories: productFormData.categories.split(',').map((c) => c.trim()).filter(Boolean),
        colors: productFormData.colors.split(',').map((c) => c.trim()).filter(Boolean),
        sizes: productFormData.sizes.split(',').map((s) => s.trim()).filter(Boolean),
        images
      };

      if (editingProduct) {
        await axios.put(`${API_URL}/admin/products/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated successfully!');
      } else {
        await axios.post(`${API_URL}/admin/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product created successfully!');
      }

      setProductDialogOpen(false);
      setEditingProduct(null);
      setProductFormData({ name: '', description: '', price: '', categories: '', colors: '', sizes: '', images_urls: [] });
      setProductFiles([]);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductFormData({
      name: product.name,
      description: product.description || '',
      price: (product.price ?? '').toString(),
      categories: (product.categories || []).join(', '),
      colors: (product.colors || []).join(', '),
      sizes: (product.sizes || []).join(', '),
      images_urls: product.images || []
    });
    setProductFiles([]);
    setProductDialogOpen(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-400 bg-green-400/10';
      case 'pending': return 'text-yellow-400 bg-yellow-400/10';
      case 'completed': return 'text-blue-400 bg-blue-400/10';
      case 'cancelled': return 'text-red-400 bg-red-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  const getTicketStatusColor = (status) => {
    switch (status) {
      case 'resolved': return 'text-green-400 bg-green-400/10';
      case 'in_progress': return 'text-yellow-400 bg-yellow-400/10';
      case 'closed': return 'text-gray-300 bg-gray-500/10';
      case 'open': return 'text-blue-400 bg-blue-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  const handleUpdateTicket = async (ticketId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/admin/support-tickets/${ticketId}`,
        {
          status,
          response: ticketNotes[ticketId] || '',
          private_note: privateTicketNotes[ticketId] || ''
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Support ticket updated');
      setTicketNotes((prev) => ({ ...prev, [ticketId]: '' }));
      setPrivateTicketNotes((prev) => ({ ...prev, [ticketId]: '' }));
      fetchSupportTickets();
    } catch (error) {
      console.error('Error updating support ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to update support ticket');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-24">
        <h1 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text" data-testid="admin-dashboard-title">
          Admin Dashboard
        </h1>
        <p className="text-gray-400 mb-12 text-lg">
          Manage designers, products, bookings, orders, and support.
        </p>

        <Tabs defaultValue="designers" className="w-full">
          <TabsList className="bg-[#1a1a1a] border border-[#d4a574]/20 mb-8">
            <TabsTrigger value="designers" className="data-[state=active]:bg-[#d4a574] data-[state=active]:text-[#0a0a0a]" data-testid="designers-tab">
              <Users className="w-4 h-4 mr-2" />
              Designers
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-[#d4a574] data-[state=active]:text-[#0a0a0a]" data-testid="products-tab">
              <Package className="w-4 h-4 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[#d4a574] data-[state=active]:text-[#0a0a0a]" data-testid="bookings-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#d4a574] data-[state=active]:text-[#0a0a0a]" data-testid="orders-tab">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="support" className="data-[state=active]:bg-[#d4a574] data-[state=active]:text-[#0a0a0a]" data-testid="support-tab">
              <MessageSquare className="w-4 h-4 mr-2" />
              Support Tickets
            </TabsTrigger>
          </TabsList>

          <TabsContent value="designers">
            <div className="flex justify-end mb-6">
              <Dialog open={designerDialogOpen} onOpenChange={(open) => {
                setDesignerDialogOpen(open);
                if (!open) {
                  setEditingDesigner(null);
                  setDesignerFormData({
                    name: '',
                    bio: '',
                    experience: '',
                    specialties: '',
                    hourly_rate: '',
                    profile_images_urls: [],
                    design_images_urls: []
                  });
                  setProfileFiles([]);
                  setDesignFiles([]);
                }
              }}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="add-designer-btn">
                    <Plus className="w-5 h-5" />
                    Add Designer
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#1a1a1a] text-white border-[#d4a574]/20 max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold gradient-text">
                      {editingDesigner ? 'Edit Designer' : 'Add New Designer'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="name" className="text-gray-300 mb-2 block">Name *</Label>
                      <Input
                        id="name"
                        value={designerFormData.name}
                        onChange={(e) => setDesignerFormData({ ...designerFormData, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        data-testid="designer-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bio" className="text-gray-300 mb-2 block">Bio *</Label>
                      <Textarea
                        id="bio"
                        value={designerFormData.bio}
                        onChange={(e) => setDesignerFormData({ ...designerFormData, bio: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        rows={3}
                        data-testid="designer-bio-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="experience" className="text-gray-300 mb-2 block">Experience *</Label>
                      <Input
                        id="experience"
                        placeholder="e.g., 6+ Years Experience"
                        value={designerFormData.experience}
                        onChange={(e) => setDesignerFormData({ ...designerFormData, experience: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        data-testid="designer-experience-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="specialties" className="text-gray-300 mb-2 block">Specialties (comma-separated) *</Label>
                      <Input
                        id="specialties"
                        placeholder="Traditional, Bridal, Western"
                        value={designerFormData.specialties}
                        onChange={(e) => setDesignerFormData({ ...designerFormData, specialties: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        data-testid="designer-specialties-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="hourly_rate" className="text-gray-300 mb-2 block">Rate per Visit (₹) *</Label>
                      <Input
                        id="hourly_rate"
                        type="number"
                        step="0.01"
                        value={designerFormData.hourly_rate}
                        onChange={(e) => setDesignerFormData({ ...designerFormData, hourly_rate: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        data-testid="designer-rate-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-2 block">Profile Images (upload one or more)</Label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => setProfileFiles(Array.from(e.target.files || []))}
                        className="w-full px-4 py-2 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        data-testid="designer-profile-files-input"
                      />
                      {designerFormData.profile_images_urls?.length > 0 && (
                        <p className="text-xs text-gray-400 mt-2">Existing: {designerFormData.profile_images_urls.length} image(s) will be kept unless you remove them programmatically.</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-2 block">Design Images (upload one or more)</Label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => setDesignFiles(Array.from(e.target.files || []))}
                        className="w-full px-4 py-2 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        data-testid="designer-design-files-input"
                      />
                      {designerFormData.design_images_urls?.length > 0 && (
                        <p className="text-xs text-gray-400 mt-2">Existing: {designerFormData.design_images_urls.length} image(s) will be kept unless you remove them programmatically.</p>
                      )}
                    </div>
                    {uploading && (
                      <div className="bg-[#0f0f0f] border border-[#d4a574]/20 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-300">Uploading To Server...</span>
                          <span className="text-sm text-[#d4a574]">{progress.current}/{progress.total}</span>
                        </div>
                        <div className="w-full bg-[#1a1a1a] rounded-full h-2">
                          <div
                            className="bg-[#d4a574] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(progress.current / progress.total) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <button
                      onClick={handleSubmitDesigner}
                      className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={uploading}
                      data-testid="save-designer-btn"
                    >
                      {uploading ? 'Uploading...' : (editingDesigner ? 'Update Designer' : 'Add Designer')}
                    </button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="designers-grid">
              {designers.map((designer) => (
                <div key={designer.id} className="glass-card overflow-hidden" data-testid={`designer-card-${designer.id}`}>
                  <div className="aspect-[3/4] relative overflow-hidden">
                    <img
                      src={(designer.profile_images && designer.profile_images[0]) || (designer.portfolio_images && designer.portfolio_images[0]) || 'https://via.placeholder.com/400x500?text=Designer'}
                      alt={designer.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-xl font-bold mb-2">{designer.name}</h3>
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2 whitespace-pre-line">{designer.bio}</p>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[#d4a574] font-bold">{Number(designer.hourly_rate || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</span>
                      <span className="text-sm text-gray-400">{designer.total_reviews} reviews</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditDesigner(designer)}
                        className="flex-1 px-3 py-2 bg-[#d4a574] text-[#0a0a0a] rounded-lg hover:bg-[#c89860] transition-colors flex items-center justify-center gap-2"
                        data-testid={`edit-designer-btn-${designer.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteDesigner(designer.id)}
                        className="flex-1 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors flex items-center justify-center gap-2"
                        data-testid={`delete-designer-btn-${designer.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="products">
            <div className="flex justify-end mb-6">
              <Dialog open={productDialogOpen} onOpenChange={(open) => {
                setProductDialogOpen(open);
                if (!open) {
                  setEditingProduct(null);
                  setProductFormData({ name: '', description: '', price: '', categories: '', images_urls: [] });
                  setProductFiles([]);
                }
              }}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="add-product-btn">
                    <Plus className="w-5 h-5" />
                    Add Product
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#1a1a1a] text-white border-[#d4a574]/20 max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold gradient-text">
                      {editingProduct ? 'Edit Product' : 'Add New Product'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="prod_name" className="text-gray-300 mb-2 block">Name *</Label>
                      <Input
                        id="prod_name"
                        value={productFormData.name}
                        onChange={(e) => setProductFormData({ ...productFormData, name: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        data-testid="product-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="prod_desc" className="text-gray-300 mb-2 block">Description</Label>
                      <Textarea
                        id="prod_desc"
                        value={productFormData.description}
                        onChange={(e) => setProductFormData({ ...productFormData, description: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        rows={3}
                        data-testid="product-description-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="prod_price" className="text-gray-300 mb-2 block">Price (₹) *</Label>
                      <Input
                        id="prod_price"
                        type="number"
                        step="0.01"
                        value={productFormData.price}
                        onChange={(e) => setProductFormData({ ...productFormData, price: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        data-testid="product-price-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="prod_categories" className="text-gray-300 mb-2 block">Categories (comma-separated)</Label>
                      <Input
                        id="prod_categories"
                        placeholder="women, bridal, accessories"
                        value={productFormData.categories}
                        onChange={(e) => setProductFormData({ ...productFormData, categories: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        data-testid="product-categories-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="prod_colors" className="text-gray-300 mb-2 block">Colours (comma-separated)</Label>
                      <Input
                        id="prod_colors"
                        placeholder="Red, Blue, Black"
                        value={productFormData.colors}
                        onChange={(e) => setProductFormData({ ...productFormData, colors: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        data-testid="product-colors-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="prod_sizes" className="text-gray-300 mb-2 block">Sizes (comma-separated)</Label>
                      <Input
                        id="prod_sizes"
                        placeholder="XS, S, M, L, XL"
                        value={productFormData.sizes}
                        onChange={(e) => setProductFormData({ ...productFormData, sizes: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        data-testid="product-sizes-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300 mb-2 block">Images (upload one or more)</Label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => setProductFiles(Array.from((e.target.files || [])))}
                        className="w-full px-4 py-2 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white"
                        data-testid="product-files-input"
                      />
                      {productFormData.images_urls?.length > 0 && (
                        <p className="text-xs text-gray-400 mt-2">Existing: {productFormData.images_urls.length} image(s) will be kept unless you remove them programmatically.</p>
                      )}
                    </div>
                    {uploading && (
                      <div className="bg-[#0f0f0f] border border-[#d4a574]/20 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-300">Uploading To Server...</span>
                          <span className="text-sm text-[#d4a574]">{progress.current}/{progress.total}</span>
                        </div>
                        <div className="w-full bg-[#1a1a1a] rounded-full h-2">
                          <div
                            className="bg-[#d4a574] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(progress.current / progress.total) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <button
                      onClick={handleSubmitProduct}
                      className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={uploading}
                      data-testid="save-product-btn"
                    >
                      {uploading ? 'Uploading...' : (editingProduct ? 'Update Product' : 'Add Product')}
                    </button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
              {products.map((product) => (
                <div key={product.id} className="glass-card overflow-hidden" data-testid={`product-card-${product.id}`}>
                  <div className="aspect-[3/4] relative overflow-hidden">
                    <img
                      src={(product.images && product.images[0]) || 'https://via.placeholder.com/400x500?text=Product'}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-xl font-bold mb-2">{product.name}</h3>
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2 whitespace-pre-line">{product.description}</p>

                    {(product.colors && product.colors.length > 0) && (
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs text-gray-400">Colours:</span>
                        {product.colors.slice(0, 5).map((c, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-[#d4a574]/10 text-[#d4a574] rounded-full text-xs">{c}</span>
                        ))}
                        {product.colors.length > 5 && (
                          <span className="text-xs text-gray-500">+{product.colors.length - 5}</span>
                        )}
                      </div>
                    )}

                    {(product.sizes && product.sizes.length > 0) && (
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className="text-xs text-gray-400">Sizes:</span>
                        {product.sizes.slice(0, 6).map((s, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-[#d4a574]/10 text-[#d4a574] rounded-full text-xs">{s}</span>
                        ))}
                        {product.sizes.length > 6 && (
                          <span className="text-xs text-gray-500">+{product.sizes.length - 6}</span>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[#d4a574] font-bold">{Number(product.price || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</span>
                      <span className="text-sm text-gray-400">{product.total_reviews || 0} reviews</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditProduct(product)}
                        className="flex-1 px-3 py-2 bg-[#d4a574] text-[#0a0a0a] rounded-lg hover:bg-[#c89860] transition-colors flex items-center justify-center gap-2"
                        data-testid={`edit-product-btn-${product.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="flex-1 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors flex items-center justify-center gap-2"
                        data-testid={`delete-product-btn-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <AdminBookingsTab
            bookings={bookings}
            getStatusColor={getStatusColor}
            expandedBookingId={expandedBookingId}
            setExpandedBookingId={setExpandedBookingId}
          />

          <TabsContent value="orders">
            {orders.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <p className="text-gray-400 text-lg">No product orders yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="glass-card p-6">
                    <div className="space-y-4">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <button
                          type="button"
                          onClick={() => setExpandedOrderId((prev) => (prev === order.id ? null : order.id))}
                          className="flex-1 text-left"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">
                            <div>
                              <p className="text-sm text-gray-400 mb-1">Order Ref</p>
                              <p className="font-semibold break-all">{order.session_id}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-400 mb-1">Customer</p>
                              <p className="font-semibold">{order.customer_name || 'Unknown'}</p>
                              <p className="text-sm text-gray-500 break-all">{order.customer_email || 'No email'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-400 mb-1">Items</p>
                              <p className="font-semibold">{order.item_summary || 'Wearoclub Order'}</p>
                              <p className="text-sm text-gray-500">{order.item_count || 0} item{order.item_count === 1 ? '' : 's'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-400 mb-1">Amount</p>
                              <p className="font-bold text-[#d4a574]">{Number(order.amount || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-400 mb-1">Status</p>
                              <span className={`px-3 py-1 rounded-full text-sm font-medium w-fit ${getStatusColor(order.status)}`}>
                                {order.status}
                              </span>
                              <p className="text-sm text-gray-500 mt-2">Payment: {order.payment_status}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-400 mb-1">Created</p>
                              <p className="font-semibold">{new Date(order.created_at).toLocaleString('en-IN')}</p>
                            </div>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpandedOrderId((prev) => (prev === order.id ? null : order.id))}
                          className="px-4 py-2 rounded-lg bg-[#d4a574]/10 text-[#d4a574] hover:bg-[#d4a574]/20 transition-colors flex items-center justify-center gap-2 shrink-0"
                        >
                          {expandedOrderId === order.id ? (
                            <>
                              <ChevronUp className="w-4 h-4" />
                              Collapse
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4" />
                              Expand
                            </>
                          )}
                        </button>
                      </div>

                      {expandedOrderId === order.id && (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pt-4 border-t border-[#d4a574]/10">
                          <div>
                            <p className="text-sm text-gray-400 mb-1">Product Order ID</p>
                            <p className="font-semibold break-all">{order.product_order_id || 'Not available'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-400 mb-1">Phone</p>
                            <p className="font-semibold">{order.phone || 'Not provided'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-400 mb-1">Currency</p>
                            <p className="font-semibold uppercase">{order.currency || 'INR'}</p>
                          </div>
                          <div className="md:col-span-2 xl:col-span-3">
                            <p className="text-sm text-gray-400 mb-1">Shipping Address</p>
                            <p className="text-gray-300 whitespace-pre-line">{order.shipping_address || 'Not provided'}</p>
                          </div>
                          <div className="md:col-span-2 xl:col-span-3">
                            <p className="text-sm text-gray-400 mb-1">Delivery Notes</p>
                            <p className="text-gray-300 whitespace-pre-line">{order.notes || 'No delivery notes provided.'}</p>
                          </div>
                          <div className="md:col-span-2 xl:col-span-3">
                            <p className="text-sm text-gray-400 mb-2">Line Items</p>
                            {order.items && order.items.length > 0 ? (
                              <div className="space-y-3">
                                {order.items.map((item, idx) => (
                                  <div key={`${order.id}-item-${idx}`} className="rounded-lg border border-[#d4a574]/10 bg-[#0f0f0f] p-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                      <div>
                                        <p className="font-semibold text-[#f0d9b5]">{item.name}</p>
                                        <p className="text-sm text-gray-400">Qty: {item.quantity}</p>
                                      </div>
                                      <div className="text-left sm:text-right">
                                        <p className="font-semibold text-white">{Number(item.line_total || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
                                        <p className="text-sm text-gray-500">Unit: {Number(item.unit_price || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500">No line items available.</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <AdminSupportTicketsTab
            supportTickets={supportTickets}
            expandedTicketId={expandedTicketId}
            setExpandedTicketId={setExpandedTicketId}
            getTicketStatusColor={getTicketStatusColor}
            ticketNotes={ticketNotes}
            setTicketNotes={setTicketNotes}
            privateTicketNotes={privateTicketNotes}
            setPrivateTicketNotes={setPrivateTicketNotes}
            handleUpdateTicket={handleUpdateTicket}
          />
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
